import { Box, Button, TextField, Tooltip } from "@leap4/frontend";
import React, { useEffect, useState } from "react";

interface IProps {
    from: string;
    to: string;
    onApply: (from: string, to: string) => void;
    /** Locked while a scan is in flight, so a second one cannot be queued on top. */
    disabled?: boolean;
}

/**
 * Dates are edited as a draft and committed on Apply. Typing into a date field fires a change per
 * keystroke, so binding it straight to the query launched a scan for every partial date.
 */
const DateRange = ({ from, to, onApply, disabled }: IProps): JSX.Element => {
    const [draftFrom, setDraftFrom] = useState(from);
    const [draftTo, setDraftTo] = useState(to);

    // Follow the applied range when it changes elsewhere — a campaign switch, or the back button.
    useEffect(() => {
        setDraftFrom(from);
        setDraftTo(to);
    }, [from, to]);

    const dirty = draftFrom !== from || draftTo !== to;
    const valid = !!draftFrom && !!draftTo && draftFrom <= draftTo;

    const apply = (): void => {
        if (dirty && valid && !disabled) onApply(draftFrom, draftTo);
    };

    const onKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === "Enter") apply();
    };

    return (
        <>
            <TextField
                label="From"
                type="date"
                size="small"
                sx={{ flexShrink: 0 }}
                value={draftFrom}
                error={!valid}
                disabled={disabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraftFrom(e.target.value)}
                onKeyDown={onKeyDown}
                InputLabelProps={{ shrink: true }}
            />
            <TextField
                label="To"
                type="date"
                size="small"
                sx={{ flexShrink: 0 }}
                value={draftTo}
                error={!valid}
                disabled={disabled}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDraftTo(e.target.value)}
                onKeyDown={onKeyDown}
                InputLabelProps={{ shrink: true }}
            />
            {/* Always rendered so applying never shifts the row. */}
            <Box sx={{ flexShrink: 0 }}>
                <Tooltip
                    arrow
                    title={
                        !valid
                            ? "From must be on or before To"
                            : dirty
                            ? "Load this range"
                            : "Range already loaded"
                    }
                >
                    <span>
                        <Button
                            size="small"
                            variant={dirty && valid ? "contained" : "outlined"}
                            disabled={!dirty || !valid || !!disabled}
                            onClick={apply}
                        >
                            Apply
                        </Button>
                    </span>
                </Tooltip>
            </Box>
        </>
    );
};

export default DateRange;
